import {
  actorEmail,
  auditStamp,
  auditValue,
  eventsOf,
  type AuditDomain,
  type AuditPage,
} from "../../api/mcp";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { Notice, Panel } from "../ui";

/**
 * "Who changed this" — the audit trail for one specific thing.
 *
 * This is the audit route worth the most: it puts the answer where the
 * question is actually asked. Nobody wonders "what happened across the fleet"
 * while looking at a merchant; they wonder who moved THIS merchant's tier.
 *
 * Two cases nothing else in the API can answer:
 *   - a blocklist row is DELETED on unblock, so this is the only record that
 *     someone was ever blocked here, and who lifted it
 *   - a cap's `updated_by` is overwritten on every change, so this is the only
 *     record of who set the value before the current one
 *
 * `target` may contain a slash (`free/get_overview_report`). It is encoded per
 * SEGMENT, not whole: the gateway route is `{target+}`, greedy, and encoding
 * the slash as %2F would defeat the greedy match it exists for.
 */
export function RecentChanges({
  config,
  domain,
  target,
  limit = 5,
}: {
  config: AppConfig;
  domain: AuditDomain;
  target: string;
  limit?: number;
}) {
  const path = `/mcp/admin/audit/${domain}/${target
    .split("/")
    .map(encodeURIComponent)
    .join("/")}`;
  const { state } = useResource<AuditPage>(config, path, [domain, target]);

  // Quiet while loading — this is a footnote, not the page. Failures are
  // named: collapsing them into "nothing recorded" would report silence
  // where the server refused or could not look.
  if (state.kind === "loading") return null;

  if (state.kind !== "ready") {
    const message =
      state.kind === "signedout"
        ? "Signed out — reload to see who changed this."
        : state.kind === "denied"
          ? state.error.message
          : state.kind === "unavailable"
            ? state.error.message
            : state.kind === "notfound"
              ? "Audit route not mounted on this gateway."
              : state.kind === "invalid"
                ? state.error.message
                : state.error.message;
    return (
      <Notice tone="amber" title="Could not read recent changes">
        {message}
      </Notice>
    );
  }

  const events = eventsOf(state.data).slice(0, limit);

  return (
    <Panel className="p-4">
      <h2 className="text-sm font-semibold">Recent changes</h2>

      {events.length === 0 ? (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Nothing recorded. Changes made before the audit log existed do not appear here.
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {events.map((e, i) => (
            <li key={`${e.timestamp}-${i}`} className="text-xs">
              <span className="text-slate-500 dark:text-slate-400">
                {auditStamp(e.timestamp, { dateOnly: true })}
              </span>{" "}
              <span>{actorEmail(e.actor)}</span>{" "}
              <span className="text-slate-500 dark:text-slate-400">{e.action}</span>{" "}
              <span className="text-slate-400 dark:text-slate-500">
                {auditValue(e.before)} → {auditValue(e.after)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
